import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Double Decision — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/double-decision.md §FTUE
 *   E1 : 1 cible centre seule (1500 ms). Facile.
 *   E2 : centre + 1 distracteur, pas de cible périphérique.
 *   E3 : centre + cible périphérique + 1 distracteur.
 *
 * Pas de scoring — pédagogique uniquement.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CENTRAL_CHOICES, CENTRAL_LABELS, eccentricityRadiusPx, } from './params';
const RADIUS = eccentricityRadiusPx['10deg'];
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'double_decision.demo.step1_title',
        hintKey: 'double_decision.demo.step1_hint',
        central: 'A',
        peripheralTarget: null,
        distractorAngles: [],
        durationMs: 1500,
    },
    {
        idx: 2,
        titleKey: 'double_decision.demo.step2_title',
        hintKey: 'double_decision.demo.step2_hint',
        central: 'B',
        peripheralTarget: null,
        distractorAngles: [90],
        durationMs: 1500,
    },
    {
        idx: 3,
        titleKey: 'double_decision.demo.step3_title',
        hintKey: 'double_decision.demo.step3_hint',
        central: 'A',
        peripheralTarget: 270,
        distractorAngles: [90],
        durationMs: 1800,
    },
];
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [showStimulus, setShowStimulus] = useState(true);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setShowStimulus(true);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('double_decision.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "relative mx-auto h-[280px] w-[280px] max-w-full", children: [_jsx("div", { className: [
                            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                            'flex h-[64px] w-[64px] items-center justify-center rounded-full border border-cyan-border bg-cyan-surface text-[32px] text-cyan-text',
                            showStimulus ? 'opacity-100' : 'opacity-40',
                            'transition-opacity duration-feedback',
                        ].join(' '), children: CENTRAL_LABELS[step.central] }), step.peripheralTarget !== null && (_jsx("div", { className: [
                            'absolute left-1/2 top-1/2 flex h-[40px] w-[40px] items-center justify-center text-[24px] text-cyan-network',
                            showStimulus ? 'opacity-100' : 'opacity-40',
                            'transition-opacity duration-feedback',
                        ].join(' '), style: {
                            transform: `translate(-50%, -50%) rotate(${step.peripheralTarget}deg) translateY(-${RADIUS}px) rotate(-${step.peripheralTarget}deg)`,
                        }, children: "\u25B2" })), step.distractorAngles.map((a) => (_jsx("div", { className: [
                            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[32px] w-[32px] rounded-full border border-cyan-network',
                            showStimulus ? 'opacity-100' : 'opacity-40',
                            'transition-opacity duration-feedback',
                        ].join(' '), style: {
                            transform: `translate(-50%, -50%) rotate(${a}deg) translateY(-${RADIUS}px) rotate(-${a}deg)`,
                        } }, a)))] }), _jsxs("div", { className: "grid w-full grid-cols-2 gap-s3 text-start text-[12px] text-cyan-text-secondary", children: [_jsxs("div", { className: "border border-cyan-border-faint bg-cyan-surface p-s3", children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('double_decision.demo.to_process') }), _jsx("span", { className: "text-[13px] text-cyan-text", children: step.peripheralTarget !== null
                                    ? t('double_decision.demo.process_central_with_target')
                                    : t('double_decision.demo.process_central_only') })] }), _jsxs("div", { className: "border border-cyan-border-faint bg-cyan-surface p-s3", children: [_jsx("span", { className: "block font-mono uppercase tracking-[0.20em] text-cyan-text-faint", children: t('double_decision.demo.to_ignore') }), _jsx("span", { className: "text-[13px] text-cyan-text", children: step.distractorAngles.length === 0
                                    ? t('double_decision.demo.ignore_none')
                                    : t('double_decision.demo.ignore_distractors_count', { count: step.distractorAngles.length }) })] })] }), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [_jsx("button", { type: "button", onClick: () => setShowStimulus((v) => !v), className: "border border-cyan-border-faint px-s4 py-s2 text-[12px] text-cyan-text-secondary hover:border-cyan-border", children: showStimulus ? t('double_decision.demo.mask_hide') : t('double_decision.demo.mask_show') }), _jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') }), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] }), _jsx("p", { className: "sr-only", children: t('double_decision.demo.aria_central_target', {
                    label: CENTRAL_LABELS[step.central],
                    central: step.central,
                    choices: CENTRAL_CHOICES.map((c) => `${c}=${CENTRAL_LABELS[c]}`).join(', '),
                }) })] }));
}
//# sourceMappingURL=demo.js.map